import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, ZoomControl, GeoJSON, useMap, CircleMarker, Tooltip, Marker, Polygon, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useGisWorker } from '../../hooks/useGisWorker';
import { useMapStore } from '../../store/useMapStore';
import type {
  GisFeature,
  PdsShop,
  Geometry,
  Point,
  Polygon as GisPolygon,
  MultiPolygon as GisMultiPolygon,
  PoliceStationProperties,
  HealthFacility,
  PoliceResolutionResult,
  LandParcel,
  TempleProperties,
  VeterinaryFacility,
  GisProperties,
  FuelStation
} from '../../types/gis';
import { getBrandColor, getBrandLabel, getBrandLogo } from '../../constants/fuel';
import type { LocalBodyV2Feature } from '../../types/gis_v2';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getOfficeTypeLabelKey } from '../../utils/postal';
import { useTranslation, type TranslationKey } from '../../i18n/translations';
import UserLocationMarker from './UserLocationMarker';
import { isWithinTamilNadu } from '../../utils/urlParser';
import { getCartoTileUrl } from '../../utils/mapTiles';
import { useLocation } from 'react-router-dom';
import { ENABLE_LAND_PARCEL } from '../../config/features';

const stopLeafletClickPropagation = (e: L.LeafletMouseEvent | L.LeafletEvent | Event) => {
  const event = 'originalEvent' in e && e.originalEvent ? e.originalEvent : e;
  L.DomEvent.stopPropagation(event as Event);
};

const fuelIconCache = new Map<string, L.DivIcon>();

function getFuelIcon(brand: string, isSelected: boolean): L.DivIcon {
  const key = `${brand}_${isSelected}`;
  if (fuelIconCache.has(key)) return fuelIconCache.get(key)!;

  const brandColor = getBrandColor(brand);
  const brandLogo = getBrandLogo(brand);
  const size = isSelected ? 32 : 28;
  const anchor = size / 2;

  const icon = L.divIcon({
    html: `
      ${isSelected ? '<div class="pulse-search"></div>' : ''}
      <div style="background: #ffffff; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2.5px solid ${brandColor}; display: flex; align-items: center; justify-content: center; box-shadow: 0 3px 10px rgba(0,0,0,0.32); position: relative; z-index: 10; overflow: hidden; padding: 2px;">
        ${brandLogo
          ? `<img src="${brandLogo}" style="width: 100%; height: 100%; object-fit: contain; border-radius: 50%;" alt="${brand}" />`
          : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${brandColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 22v-8"></path><path d="M7 22v-8"></path><path d="M14 22v-8"></path><path d="M18 22v-8"></path><path d="M12 2v20"></path><path d="M6 10h12"></path><path d="M6 14h12"></path>
             </svg>`
        }
      </div>`,
    className: 'custom-fuel-icon',
    iconSize: [size, size],
    iconAnchor: [anchor, anchor]
  });

  fuelIconCache.set(key, icon);
  return icon;
}

const MapController: React.FC<{
  result: GisFeature | null;
  geometry: Geometry | null;
  policeResolution: PoliceResolutionResult | null;
  selectedLocalBodyV2: LocalBodyV2Feature | null;
  selectedHealthFacility: HealthFacility | null;
  selectedVeterinaryFacility: VeterinaryFacility | null;
  selectedRegistrationOffice: GisFeature | null;
  selectedLandParcel: LandParcel | null;
  selectedTemple: TempleProperties | null;
  selectedFuelStation: GisFeature | null;
  view: { center: [number, number]; zoom: number };
}> = ({ result, geometry, policeResolution, selectedLocalBodyV2, selectedHealthFacility, selectedVeterinaryFacility, selectedRegistrationOffice, selectedLandParcel, selectedTemple, selectedFuelStation, view }) => {
  const map = useMap();
  const location = useLocation();
  const { isSidebarOpen, activeDistrict, districtsData } = useMapStore();

  // Sync with store view changes
  useEffect(() => {
    if (!map) return;
    map.setView(view.center, view.zoom, { animate: true });
  }, [map, view]);

  useEffect(() => {
    if (!map) return;

    const isMobile = window.innerWidth <= 768;
    const leftPad = isMobile ? 20 : (isSidebarOpen ? 340 : 80);
    const bottomPad = isMobile ? 320 : 80; // Space for bottom sheet

    if (result && result.geometry) {
      try {
        let resultBounds: L.LatLngBounds;
        const props = result.properties as GisProperties & {
          bounds?: { southwest: { lat: number; lng: number }; northeast: { lat: number; lng: number } };
          viewport?: { southwest: { lat: number; lng: number }; northeast: { lat: number; lng: number } };
        };

        if (props.bounds) {
          resultBounds = L.latLngBounds(
            [props.bounds.southwest.lat, props.bounds.southwest.lng],
            [props.bounds.northeast.lat, props.bounds.northeast.lng]
          );
        } else if (props.viewport) {
          resultBounds = L.latLngBounds(
            [props.viewport.southwest.lat, props.viewport.southwest.lng],
            [props.viewport.northeast.lat, props.viewport.northeast.lng]
          );
        } else {
          resultBounds = L.geoJSON(result).getBounds();
        }

        if (resultBounds.isValid()) {
          map.flyToBounds(resultBounds, {
            paddingTopLeft: [leftPad, 120],
            paddingBottomRight: [80, bottomPad],
            maxZoom: 14,
            duration: 0.8
          });
        }
      } catch (e) {
        console.warn('Could not fly to bounds for result', e);
      }
    } else if (selectedLocalBodyV2) {
      try {
        const wardGeom = selectedLocalBodyV2.properties?.ward_geometry;
        const targetGeom = wardGeom || selectedLocalBodyV2.geometry;

        if (targetGeom) {
          const bounds = L.geoJSON(targetGeom).getBounds();
          if (bounds.isValid()) {
            map.flyToBounds(bounds, {
              paddingTopLeft: [leftPad, 120],
              paddingBottomRight: [80, bottomPad],
              maxZoom: wardGeom ? 16 : 14, // Allow tighter zoom for specific Wards
              duration: 0.8
            });
          }
        }
      } catch (e) {
        console.warn('Could not fly to bounds for V2 result', e);
      }
    } else if (policeResolution && !policeResolution.isBoundaryValid && policeResolution.station) {
      // Fly to station point if boundary is invalid
      const [lng, lat] = policeResolution.station.geometry.coordinates;
      map.flyTo([lat, lng], 14, { duration: 0.8 });
    } else if (geometry) {
      try {
        const bounds = L.geoJSON(geometry).getBounds();
        if (bounds.isValid()) {
          // Aggressive padding to force the selection into the visible 'hole'
          map.flyToBounds(bounds, {
            paddingTopLeft: [leftPad, 150], // Extra top padding for search bar
            paddingBottomRight: [isMobile ? 20 : 420, bottomPad], // Extra right padding for result card
            maxZoom: 14,
            duration: 0.8
          });
        }
      } catch (e) {
        console.warn('Could not fly to bounds for geometry', e);
      }
    } else if (selectedHealthFacility && selectedHealthFacility.geometry) {
      try {
        const [lng, lat] = selectedHealthFacility.geometry.coordinates;
        // Only fly to if we aren't already centered on it (approx)
        const center = map.getCenter();
        const dist = Math.sqrt(Math.pow(center.lat - lat, 2) + Math.pow(center.lng - lng, 2));
        if (dist > 0.001 || map.getZoom() < 14) {
          map.flyTo([lat, lng], 15, { duration: 0.8 });
        }
      } catch (e) {
        console.warn('Could not fly to health facility', e);
      }
    } else if (selectedVeterinaryFacility && selectedVeterinaryFacility.geometry) {
      try {
        const [lng, lat] = selectedVeterinaryFacility.geometry.coordinates;
        map.flyTo([lat, lng], 15, { duration: 0.8 });
      } catch (e) {
        console.warn('Could not fly to veterinary facility', e);
      }
    } else if (selectedRegistrationOffice && selectedRegistrationOffice.geometry) {
      try {
        const geom = selectedRegistrationOffice.geometry;
        const bounds = L.geoJSON(geom).getBounds();
        if (bounds.isValid()) {
          map.flyToBounds(bounds, {
            paddingTopLeft: [leftPad, 150],
            paddingBottomRight: [isMobile ? 20 : 420, bottomPad],
            maxZoom: 14,
            duration: 0.8
          });
        }
      } catch (e) {
        console.warn('Could not fly to registration bounds', e);
      }
    } else if (selectedFuelStation && selectedFuelStation.geometry) {
      try {
        const [lng, lat] = selectedFuelStation.geometry.coordinates as [number, number];
        const center = map.getCenter();
        const dist = Math.sqrt(Math.pow(center.lat - lat, 2) + Math.pow(center.lng - lng, 2));
        if (dist > 0.001 || map.getZoom() < 14) {
          map.flyTo([lat, lng], 15, { duration: 0.8 });
        }
      } catch (e) {
        console.warn('Could not fly to fuel station', e);
      }
    } else if (ENABLE_LAND_PARCEL && selectedLandParcel && selectedLandParcel.geometry) {
      try {
        const bounds = L.geoJSON(selectedLandParcel.geometry).getBounds();
        if (bounds.isValid()) {
          map.flyToBounds(bounds, {
            paddingTopLeft: [leftPad, 150],
            paddingBottomRight: [isMobile ? 20 : 420, bottomPad],
            maxZoom: 19,
            duration: 0.8
          });
        }
      } catch (e) {
        console.warn('Could not fly to land parcel bounds', e);
      }
    } else if (selectedTemple) {
      try {
        const lat = typeof selectedTemple.g.lat === 'number' ? selectedTemple.g.lat : parseFloat(selectedTemple.g.lat);
        const lng = typeof selectedTemple.g.lng === 'number' ? selectedTemple.g.lng : parseFloat(selectedTemple.g.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          map.flyTo([lat, lng], 15, { duration: 0.8 });
        }
      } catch (e) {
        console.warn('Could not fly to temple location', e);
      }
    } else if (activeDistrict && districtsData) {
      // Zoom to district if no specific result is selected
      const districtFeature = districtsData.features.find(f => {
        const name = f.properties.district_n || f.properties.district || f.properties.DISTRICT || f.properties.NAME;
        return name?.toString().toLowerCase() === activeDistrict.toLowerCase();
      });

      if (districtFeature) {
        try {
          const pathParts = location.pathname.split('/').filter(Boolean);
          const routeDistrict = pathParts[1] ? decodeURIComponent(pathParts[1]) : '';
          const isDistrictDeepLink = pathParts.length >= 2 && !/^\d{6}$/.test(routeDistrict);

          const bounds = L.geoJSON(districtFeature).getBounds();
          if (bounds.isValid()) {
            map.flyToBounds(bounds, {
              paddingTopLeft: [leftPad, isDistrictDeepLink ? 70 : 100],
              paddingBottomRight: [isDistrictDeepLink ? 40 : 80, isDistrictDeepLink ? 50 : 80],
              maxZoom: isDistrictDeepLink ? 11 : undefined,
              duration: 0.8
            });
          }
        } catch (e) {
          console.warn('Could not fly to district bounds', e);
        }
      }
    }
    
  }, [result, geometry, map, isSidebarOpen, policeResolution, activeDistrict, districtsData, selectedHealthFacility, selectedLocalBodyV2, selectedVeterinaryFacility, selectedRegistrationOffice, selectedLandParcel, selectedTemple, selectedFuelStation, location.pathname]);
  return null;
};

const MapEvents: React.FC<{ onResolve: (lat: number, lng: number, layer: string) => void; activeLayer: string; onInteract?: () => void }> = ({ onResolve, activeLayer, onInteract }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const onClick = (e: L.LeafletMouseEvent) => {
      if (onInteract) onInteract();
      onResolve(e.latlng.lat, e.latlng.lng, activeLayer);
    };

    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
    };
  }, [map, onResolve, activeLayer, onInteract]);

  return null;
};

interface EnrichedTemple extends TempleProperties {
  tier?: 'T1' | 'T2' | 'T3';
  transaction_count?: number;
  device_count?: number;
}

const TempleMarkers: React.FC = () => {
  const { selectedTemples, selectedTemple, templeFilters, language } = useMapStore();

  const getTempleIcon = useCallback((t: EnrichedTemple) => {
    // 1. Color (Age)
    let color = '#86198f'; // Purple for newer/unknown
    const cen = t.c?.hist?.en?.cen;
    if (cen) {
      const c = String(cen).trim().toLowerCase();
      const isPre10th = /before|(\b(1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)\b)/i.test(c) && 
                        !/(\b(11th|12th|13th|14th|15th|16th|17th|18th|19th|20th|21st)\b)/i.test(c);
      const isMedieval = /(\b(11th|12th|13th|14th|15th)\b)/i.test(c);
      
      if (isPre10th) {
        color = '#c2410c'; // Terracotta/Rust
      } else if (isMedieval) {
        color = '#78350f'; // Dark Ochre/Brown
      }
    }

    // 2. Border (Heritage)
    const isHeritage = t.c?.hist?.en?.her === 'Yes' || t.c?.hist?.en?.her === 'Y';
    const borderStyle = isHeritage 
      ? 'border: 2px solid #eab308; box-shadow: 0 0 10px #eab308, 0 2px 6px rgba(0,0,0,0.3);' 
      : 'border: 1.5px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.25);';

    // 3. Pulse (Activity)
    const isActiveHub = (t.transaction_count || 0) > 10000;
    const pulseClass = isActiveHub ? 'pulse-active-temple' : '';

    return L.divIcon({
      html: `
        <div class="${pulseClass}" style="width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; background: transparent; position: relative;">
          <div style="background: ${color}; width: 25px; height: 25px; border-radius: 50%; ${borderStyle} display: flex; align-items: center; justify-content: center; position: relative; z-index: 1;">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="22" x2="21" y2="22"></line>
              <line x1="6" y1="18" x2="6" y2="11"></line>
              <line x1="10" y1="18" x2="10" y2="11"></line>
              <line x1="14" y1="18" x2="14" y2="11"></line>
              <line x1="18" y1="18" x2="18" y2="11"></line>
              <polygon points="12 2 20 7 4 7"></polygon>
            </svg>
          </div>
        </div>`,
      className: 'custom-temple-icon',
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    });
  }, []);

  const memoizedTempleMarkers = useMemo(() => {
    if (!selectedTemples) return null;

    const isAnyFilterActive = templeFilters.relics || templeFilters.heritage || templeFilters.activeHubs;

    return (selectedTemples as EnrichedTemple[])
      .filter((t: EnrichedTemple) => {
        if (selectedTemple && t.id === selectedTemple.id) return false;
        
        if (!isAnyFilterActive) return true;

        let matches = false;

        if (templeFilters.relics) {
          const cen = t.c?.hist?.en?.cen;
          if (cen) {
            const c = String(cen).trim().toLowerCase();
            const isRelic = /before|(\b(1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)\b)/i.test(c) && 
                            !/(\b(11th|12th|13th|14th|15th|16th|17th|18th|19th|20th|21st)\b)/i.test(c);
            if (isRelic) matches = true;
          }
        }

        if (templeFilters.heritage) {
          const isHeritage = t.c?.hist?.en?.her === 'Yes' || t.c?.hist?.en?.her === 'Y';
          if (isHeritage) matches = true;
        }

        if (templeFilters.activeHubs) {
          const isActiveHub = (t.transaction_count || 0) > 10000;
          if (isActiveHub) matches = true;
        }

        return matches;
      })
      .map((t: EnrichedTemple, i: number) => {
        const lat = typeof t.g.lat === 'number' ? t.g.lat : parseFloat(t.g.lat);
        const lng = typeof t.g.lng === 'number' ? t.g.lng : parseFloat(t.g.lng);
        if (isNaN(lat) || isNaN(lng)) return null;

        const name = language === 'ta' ? t.nm.tn : t.nm.en;
        const deity = language === 'ta' ? t.c?.mol?.tn : t.c?.mol?.en;
        const cenText = language === 'ta' ? t.c?.hist?.tn?.cen : t.c?.hist?.en?.cen;
        const district = language === 'ta' ? t.dist.tn : t.dist.en;

        return (
          <Marker
            key={`temple-marker-${t.id}-${i}`}
            position={[lat, lng]}
            icon={getTempleIcon(t)}
            keyboard={true}
            bubblingMouseEvents={false}
            alt={t.nm.en}
            title={t.nm.en}
            eventHandlers={{
              click: (e) => {
                stopLeafletClickPropagation(e);
                const store = useMapStore.getState();
                store.setNoDataFound(false);
                store.setSelectedTemple(t);
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <div style={{ padding: '6px 10px', fontSize: '12px', minWidth: '160px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px', color: 'var(--text-primary)', lineHeight: 1.3 }}>{name}</div>
                {deity && (
                  <div style={{ opacity: 0.9, fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {language === 'ta' ? 'மூலவர்: ' : 'Deity: '}<strong>{deity}</strong>
                  </div>
                )}
                {cenText && (
                  <div style={{ opacity: 0.9, fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {language === 'ta' ? 'நூற்றாண்டு: ' : 'Era: '}<strong>{cenText}</strong>
                  </div>
                )}
                {!deity && !cenText && (
                  <div style={{ opacity: 0.8, fontSize: '11px', color: 'var(--text-secondary)' }}>{district}</div>
                )}
              </div>
            </Tooltip>
          </Marker>
        );
      });
  }, [selectedTemples, selectedTemple, getTempleIcon, templeFilters, language]);

  return <>{memoizedTempleMarkers}</>;
};

const TempleLegend: React.FC<{
  theme: string;
  language: string;
  t: (key: TranslationKey) => string;
}> = ({ theme, language, t }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const { templeFilters, setTempleFilters } = useMapStore();
  const legendRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (legendRef.current) {
      L.DomEvent.disableClickPropagation(legendRef.current);
      L.DomEvent.disableScrollPropagation(legendRef.current);
    }
  }, []);

  const themeClass = theme === 'dark' ? 'temple-legend--dark' : 'temple-legend--light';

  return (
    <div
      ref={legendRef}
      className={`temple-legend ${themeClass}`}
    >
      <div
        className="temple-legend__header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="temple-legend__title">
          {t('MAP_HIERARCHY') || 'Temple Explorer'}
        </span>
        <span className="temple-legend__toggle">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {isExpanded && (
        <div className="temple-legend__body">
          {/* Legend Swatches */}
          <div className={`temple-legend__swatches ${theme === 'dark' ? 'temple-legend__swatches--dark' : 'temple-legend__swatches--light'}`}>
            <div className="temple-legend__swatch">
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#c2410c', border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', flexShrink: 0 }} />
              <span className="temple-legend__swatch-label">
                {language === 'ta' ? 'பண்டைய சின்னம் (<10ச.)' : 'Ancient Relics (Pre-10th C.)'}
              </span>
            </div>
            <div className="temple-legend__swatch">
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#78350f', border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', flexShrink: 0 }} />
              <span className="temple-legend__swatch-label">
                {language === 'ta' ? 'மத்திய கால தலம் (11-15ச.)' : 'Medieval Temples (11-15th C.)'}
              </span>
            </div>
            <div className="temple-legend__swatch">
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#86198f', border: '1px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', flexShrink: 0 }} />
              <span className="temple-legend__swatch-label">
                {language === 'ta' ? 'புதிய / மற்றவை' : 'Modern / Other Temples'}
              </span>
            </div>
            <div className="temple-legend__swatch">
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#86198f', border: '2px solid #eab308', boxShadow: '0 0 5px #eab308', flexShrink: 0 }} />
              <span className="temple-legend__swatch-label">
                {language === 'ta' ? 'அறநிலையத்துறை பாரம்பரியம்' : 'Official HRCE Heritage'}
              </span>
            </div>
          </div>

          {/* Interactive Checkbox Filters */}
          <div className={`temple-legend__filters ${theme === 'dark' ? 'temple-legend__filters--dark' : 'temple-legend__filters--light'}`}>
            <span className="temple-legend__filter-title">
              {language === 'ta' ? 'வடிகட்டிகள்' : 'Explorer Mindsets'}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label className="temple-legend__filter-label">
                <input
                  type="checkbox"
                  checked={templeFilters.relics}
                  onChange={(e) => setTempleFilters({ relics: e.target.checked })}
                  style={{ accentColor: '#c2410c' }}
                />
                <span className="temple-legend__filter-text">
                  <span>🏛️</span>
                  <span>{language === 'ta' ? 'வரலாற்றுச் சின்னங்கள் (<10ச.)' : 'Historical Relics'}</span>
                </span>
              </label>

              <label className="temple-legend__filter-label">
                <input
                  type="checkbox"
                  checked={templeFilters.heritage}
                  onChange={(e) => setTempleFilters({ heritage: e.target.checked })}
                  style={{ accentColor: '#eab308' }}
                />
                <span className="temple-legend__filter-text">
                  <span>🎖️</span>
                  <span>{language === 'ta' ? 'அறநிலையத்துறை பாரம்பரியம்' : 'Sacred Heritage'}</span>
                </span>
              </label>

              <label className="temple-legend__filter-label">
                <input
                  type="checkbox"
                  checked={templeFilters.activeHubs}
                  onChange={(e) => setTempleFilters({ activeHubs: e.target.checked })}
                  style={{ accentColor: '#3b82f6' }}
                />
                <span className="temple-legend__filter-text">
                  <span>🔥</span>
                  <span>{language === 'ta' ? 'சுறுசுறுப்பான தலம் (>10ஆ. பரி.)' : 'Active Hubs (>10k Txns)'}</span>
                </span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const GisMap: React.FC = () => {
  const { isReady, loadDistricts, loadStateBoundary, loadPincodes, loadTnebStatewide, loadTnebDistrict, loadPds, loadPdsIndex, loadConstituencies, loadPoliceData, loadHealthManifest, loadHealthPriority, loadHealthDistrict, loadHealthSearchIndex, loadLocalBodiesV2, loadVeterinaryData, loadRegistration, loadRadioData, resolveLocation, getSuggestions, selectSuggestion, resolveHealthFacility, resolveFuelStation, resolveLandParcel } = useGisWorker();
  const { activeLayer, view, searchQuery, searchResult, pdsData, activeDistrict, jurisdictionDetails, jurisdictionGeometry, districtsData, stateBoundaryData, acData, pcData, constituencyType, selectedPoliceStation, policeResolution, policeStationsData, selectedPdsShop, setSelectedPdsShop, theme, selectedSuggestion, setSelectedSuggestion, triggerLocateMe, setTriggerLocateMe, isResolving, setIsLocating, isLocating, setSearchSuggestions, isUserTyping, setUserTyping, selectedPostalOffices, setSelectedPostalOffice, selectedPostalOffice, healthPriorityData, healthDistrictData, selectedHealthFacility, setSelectedHealthFacility, healthScope, isHealthLoading, selectedLocalBodyV2, veterinaryData, selectedVeterinaryFacility, setSelectedVeterinaryFacility, veterinaryFilters, selectedRegistrationOffice, globalLocation, setView, setUserLocation, userLocation, autoLocateAttempted, setAutoLocateAttempted, locateTriggerSource, setLocateTriggerSource, election, pendingPincodeResolve, setPendingPincodeResolve, pendingVeterinaryResolve, setPendingVeterinaryResolve, pendingRegistrationResolve, setPendingRegistrationResolve, pendingLandParcelResolve, setPendingLandParcelResolve, noDataFound, setNoDataFound, selectedLandParcel, pendingTempleResolve, setPendingTempleResolve, selectedTemple, selectedTemples, radioData, selectedRadioStation, fuelDistrictData, selectedFuelStation, fuelRadiusKm } = useMapStore();
  const { results: electionResults } = election;
  const { t, language } = useTranslation();
  const [showDeepLinkHint, setShowDeepLinkHint] = React.useState(false);

  useEffect(() => {
    if (isReady) {
      // Stagger worker initializations to prevent blocking the main thread (TBT)
      // Core bounds load immediately
      loadDistricts();
      loadStateBoundary();
      loadPincodes();

      // Delay heavy/layer-specific indexing
      setTimeout(() => {
        loadConstituencies();
        loadPdsIndex();
      }, 500);

      setTimeout(() => {
        loadTnebStatewide();
        loadPoliceData();
        loadRadioData();
      }, 1000);

      setTimeout(() => {
        loadHealthManifest();
        loadHealthPriority();
        loadHealthSearchIndex();
      }, 1500);
    }
  }, [isReady, loadDistricts, loadStateBoundary, loadPincodes, loadTnebStatewide, loadPdsIndex, loadConstituencies, loadPoliceData, loadHealthManifest, loadHealthPriority, loadHealthSearchIndex]);

  // Handle TNEB auto-load when active district is set and layer is TNEB
  useEffect(() => {
    if (activeLayer === 'TNEB' && activeDistrict) {
      loadTnebDistrict(activeDistrict);
    }
  }, [activeLayer, activeDistrict, loadTnebDistrict]);

  // Handle Search Trigger (Pincode or Text) - only if user is actively typing with 300ms debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isUserTyping && searchQuery && searchQuery.length >= 3) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      searchTimeoutRef.current = setTimeout(() => {
        getSuggestions(searchQuery, activeLayer);
      }, 300);
    } else if (!isUserTyping || !searchQuery) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      setSearchSuggestions([]);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, activeLayer, isUserTyping, getSuggestions, setSearchSuggestions]);

  // Handle Global Location Resolver
  useEffect(() => {
    if (globalLocation && globalLocation.lat !== null && globalLocation.lng !== null) {
      console.log('[GisMap] Global Location Resolved:', globalLocation);
      resolveLocation(globalLocation.lat, globalLocation.lng, activeLayer, false, undefined, undefined, undefined, true);

      // Update map view and user marker
      setView([globalLocation.lat, globalLocation.lng], 15);
      setUserLocation({ lat: globalLocation.lat, lng: globalLocation.lng });
      setAutoLocateAttempted(true);
    }
  }, [globalLocation, activeLayer, resolveLocation, setView, setUserLocation, setAutoLocateAttempted]);

  // Handle Deep Link Pincode Resolution
  useEffect(() => {
    if (isReady && pendingPincodeResolve) {
      const pin = pendingPincodeResolve;
      // Clear instantly to prevent subsequent trigger loops
      setPendingPincodeResolve(null);
      setAutoLocateAttempted(true);

      // For Police, TNEB, Constituency, Local Bodies and PDS, a pincode deep link should simply 
      // show the pincode boundary (as SearchResult) and not commit to a specific sub-jurisdiction resolver yet.
      const needsPincodeBoundaryOnly = ['POLICE', 'TNEB', 'PDS', 'CONSTITUENCY', 'LOCAL_BODIES_V2'].includes(activeLayer);
      const effectiveLayer = needsPincodeBoundaryOnly ? 'PINCODE' : activeLayer;

      if (needsPincodeBoundaryOnly) {
        setTimeout(() => setShowDeepLinkHint(true), 0);
      }

      resolveLocation(0, 0, effectiveLayer, false, pin, undefined, undefined, true);
    }
  }, [isReady, pendingPincodeResolve, activeLayer, resolveLocation, setPendingPincodeResolve, setAutoLocateAttempted]);

  // Handle Deep Link Veterinary Facility Resolution
  useEffect(() => {
    if (isReady && pendingVeterinaryResolve) {
      const key = pendingVeterinaryResolve;
      setPendingVeterinaryResolve(null);
      setAutoLocateAttempted(true);
      resolveLocation(0, 0, 'VETERINARY', false, undefined, undefined, key, true);
    }
  }, [isReady, pendingVeterinaryResolve, resolveLocation, setPendingVeterinaryResolve, setAutoLocateAttempted]);
  
  // Handle Deep Link Registration Office Resolution
  useEffect(() => {
    if (isReady && pendingRegistrationResolve) {
      const key = pendingRegistrationResolve;
      setPendingRegistrationResolve(null);
      setAutoLocateAttempted(true);
      resolveLocation(0, 0, 'REGISTRATION', false, undefined, undefined, undefined, true, key);
    }
  }, [isReady, pendingRegistrationResolve, resolveLocation, setPendingRegistrationResolve, setAutoLocateAttempted]);

  // Handle Deep Link Temple Resolution
  useEffect(() => {
    if (isReady && pendingTempleResolve) {
      const key = pendingTempleResolve;
      setPendingTempleResolve(null);
      setAutoLocateAttempted(true);
      resolveLocation(0, 0, 'TEMPLES', false, undefined, undefined, undefined, true, undefined, key);
    }
  }, [isReady, pendingTempleResolve, resolveLocation, setPendingTempleResolve, setAutoLocateAttempted]);

  // Handle Deep Link Land Parcel Resolution
  useEffect(() => {
    if (ENABLE_LAND_PARCEL && isReady && pendingLandParcelResolve) {
      const coords = pendingLandParcelResolve;
      setPendingLandParcelResolve(null);
      setAutoLocateAttempted(true);
      
      const [lat, lng] = coords.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        setView([lat, lng], 18);
        resolveLandParcel(lat, lng);
      }
    }
  }, [isReady, pendingLandParcelResolve, resolveLandParcel, setView, setPendingLandParcelResolve, setAutoLocateAttempted]);

  // Handle Suggestion Selection
  useEffect(() => {
    if (selectedSuggestion) {
      const suggestion = selectedSuggestion;
      setSelectedSuggestion(null); // Clear it immediately after capturing
      setUserTyping(false);
      selectSuggestion(suggestion, activeLayer);
    }
  }, [selectedSuggestion, activeLayer, selectSuggestion, setSelectedSuggestion, setUserTyping]);

  // Handle Geolocation
  useEffect(() => {
    if (triggerLocateMe) {
      setTriggerLocateMe(false); // Clear trigger immediately to prevent infinite loops

      if (navigator.geolocation) {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;

            if (!isWithinTamilNadu(latitude, longitude)) {
              alert(t('OUTSIDE_TN'));
              setIsLocating(false);
              return;
            }

            // Enhanced logic: handle different layers for location
            if (activeLayer === 'LAND_PARCEL') {
                resolveLandParcel(latitude, longitude);
            } else if (activeLayer === 'RADIO') {
                resolveLocation(latitude, longitude, 'DISTRICT', false, undefined, undefined, undefined, true);
            } else {
                resolveLocation(latitude, longitude, activeLayer, false, undefined, undefined, undefined, true);
            }
            
            if (activeLayer !== 'RADIO') {
              setUserLocation({ lat: latitude, lng: longitude });
              setView([latitude, longitude], 15);
            }
            setIsLocating(false);
            setLocateTriggerSource(null);
          },
          (error) => {
            console.error("Error getting location:", error);
            setIsLocating(false);
            
            // If it's a manual request, always show an alert on error
            // If it's an auto request, only alert if it's NOT a permission denial
            if (locateTriggerSource === 'manual') {
              if (error.code === error.PERMISSION_DENIED) {
                alert(t('LOC_PERM_DENIED'));
              } else {
                alert(t('LOC_RETRIEVE_ERROR'));
              }
            } else if (error.code !== error.PERMISSION_DENIED) {
              alert(t('LOC_RETRIEVE_ERROR'));
            }
            setLocateTriggerSource(null); // Reset after handling
          }
        );
      } else {
        alert(t('LOC_NOT_SUPPORTED'));
      }
    }
  }, [triggerLocateMe, activeLayer, resolveLocation, setTriggerLocateMe, setIsLocating, t, setUserLocation, setView, locateTriggerSource, setLocateTriggerSource, resolveLandParcel]);
  
  // Handle Initial Auto-Locate Trigger
  useEffect(() => {
    // Only auto-locate if:
    // 1. Worker is ready
    // 2. We haven't already attempted it in this session
    // 3. We aren't currently resolving a deep link or global location from URL
    // 4. We don't have an active district (district-level deep link)
    // 5. Active layer is not one that intentionally skips auto-locate
    if (isReady && !autoLocateAttempted && !userLocation && !pendingPincodeResolve && !pendingVeterinaryResolve && !pendingRegistrationResolve && !pendingLandParcelResolve && !globalLocation && !activeDistrict && activeLayer !== 'RADIO') {
      console.log('[AutoLocate] Triggering initial GPS request for immediate discovery');
      setAutoLocateAttempted(true);
      setLocateTriggerSource('auto');
      setTriggerLocateMe(true);
    }
  }, [isReady, autoLocateAttempted, userLocation, pendingPincodeResolve, pendingVeterinaryResolve, pendingRegistrationResolve, pendingLandParcelResolve, globalLocation, activeDistrict, activeLayer, setAutoLocateAttempted, setTriggerLocateMe, setLocateTriggerSource]);

  // Handle Seamless Cross-Module Re-resolution
  useEffect(() => {
    // If the user has already provided their location, and they switch layers, 
    // automatically re-resolve for the new layer so they see relevant data immediately.
    // CRITICAL: Only trigger if we aren't currently resolving a manual search/selection
    // and NOT if we are in Health module with a non-pincode scope (State/District)
    // or Veterinary module (which is statewide) to prevent forcing the map back 
    // to user location when they want to see broader data.
    const isHealthInBroadScope = activeLayer === 'HEALTH' && healthScope !== 'PINCODE';
    const isVeterinary = activeLayer === 'VETERINARY';
    const isLandParcel = activeLayer === 'LAND_PARCEL';
    const isCivic = activeLayer === 'REGISTRATION' || activeLayer === 'LOCAL_BODIES_V2' || activeLayer === 'CONSTITUENCY';
    const isRadio = activeLayer === 'RADIO';
    
    // Check if we have ANY valid result across all modules
    const hasSelection = !!searchResult
      || !!jurisdictionDetails
      || !!selectedLocalBodyV2
      || !!selectedHealthFacility
      || !!selectedVeterinaryFacility
      || !!selectedRegistrationOffice
      || !!policeResolution
      || !!selectedPoliceStation
      || !!selectedPdsShop
      || !!selectedPostalOffice
      || !!selectedLandParcel
      || !!selectedTemple
      || !!selectedTemples
      || !!selectedFuelStation
      || noDataFound;

    if (isReady && userLocation && !hasSelection && !isResolving && !isLocating && !isHealthInBroadScope && !isVeterinary && !isCivic && !isLandParcel && !isRadio) {
      console.log('[AutoLocate] Re-resolving for new layer context:', activeLayer);
      resolveLocation(userLocation.lat, userLocation.lng, activeLayer, false, undefined, undefined, undefined, true);
    }
  }, [activeLayer, userLocation, isReady, resolveLocation, searchResult, jurisdictionDetails, selectedLocalBodyV2, selectedHealthFacility, selectedVeterinaryFacility, selectedRegistrationOffice, policeResolution, selectedPoliceStation, selectedPdsShop, selectedPostalOffice, selectedLandParcel, selectedTemple, selectedTemples, selectedFuelStation, noDataFound, isResolving, isLocating, healthScope]);

  // Auto-trigger PDS load on layer switch
  useEffect(() => {
    if (activeLayer === 'PDS' && searchResult) {
      const district = searchResult.properties.district || searchResult.properties.DISTRICT || searchResult.properties.DISTRICT_NAME || searchResult.properties.NAME || searchResult.properties.district_n;
      if (district) {
        loadPds(district as string, searchResult.geometry);
      }
    }
  }, [activeLayer, searchResult, loadPds]);

  // Auto-trigger Local Bodies V2 base load
  useEffect(() => {
    if (isReady && activeLayer === 'LOCAL_BODIES_V2') {
      loadLocalBodiesV2();
    }
  }, [isReady, activeLayer, loadLocalBodiesV2]);

  // Auto-trigger Veterinary load on layer switch, scoped to wherever the user currently is
  useEffect(() => {
    if (isReady && activeLayer === 'VETERINARY') {
      const resultDistrict = searchResult
        ? (searchResult.properties.district || searchResult.properties.DISTRICT || searchResult.properties.DISTRICT_NAME || searchResult.properties.district_n || searchResult.properties.NAME)
        : null;
      const district = (activeDistrict || resultDistrict)?.toString() || null;
      const coords = userLocation
        || (globalLocation?.lat != null && globalLocation?.lng != null ? { lat: globalLocation.lat, lng: globalLocation.lng } : null);
      loadVeterinaryData(district, coords);
    }
  }, [isReady, activeLayer, activeDistrict, searchResult, userLocation, globalLocation, loadVeterinaryData]);

  // Auto-trigger Registration load on layer switch
  useEffect(() => {
    if (isReady && activeLayer === 'REGISTRATION') {
      loadRegistration();
    }
  }, [isReady, activeLayer, loadRegistration]);

  const handleResolve = useCallback(
    (lat: number, lng: number, layer: string) => {
      // Gate all lookups to Tamil Nadu
      if (!isWithinTamilNadu(lat, lng)) {
        // Special case for Land Parcel: show a subtle hint if they click outside
        if (layer === 'LAND_PARCEL') {
          setNoDataFound(true, { lat, lng });
        }
        return;
      }

      if (layer === 'LAND_PARCEL') {
        if (ENABLE_LAND_PARCEL) {
          resolveLandParcel(lat, lng);
        }
      } else {
        resolveLocation(lat, lng, layer);
      }
    },
    [resolveLocation, resolveLandParcel, setNoDataFound]
  );

  const isAreaSelected = !!searchResult || !!jurisdictionGeometry || (ENABLE_LAND_PARCEL && !!selectedLandParcel);

  const districtStyle = {
    fillColor: theme === 'dark' ? '#1e293b' : '#cbd5e1',
    weight: 1.5,
    opacity: isAreaSelected ? 0.3 : 0.6,
    color: theme === 'dark' ? '#475569' : '#94a3b8',
    fillOpacity: isAreaSelected ? 0.05 : 0.1,
    interactive: false
  };

  const pincodeStyle = {
    fillColor: '#3b82f6',
    weight: 3,
    opacity: 1,
    color: '#2563eb',
    fillOpacity: 0.1,
    interactive: false
  };

  const jurisdictionStyle = {
    fillColor: '#f59e0b',
    weight: 3,
    opacity: 1,
    color: '#d97706',
    fillOpacity: 0.15,
    dashArray: '5, 5',
    interactive: false
  };

  const registrationStyle = {
    fillColor: '#6366f1',
    weight: 3,
    opacity: 0.8,
    color: '#4f46e5',
    fillOpacity: 0.15,
    dashArray: '5, 5',
    interactive: false
  };

  const getConstituencyStyle = useCallback(() => {
    return {
      fillColor: '#6366f1',
      weight: 1.5,
      opacity: isAreaSelected ? 0.3 : 0.6,
      color: '#4f46e5',
      fillOpacity: isAreaSelected ? 0.05 : 0.1,
      interactive: false
    };
  }, [isAreaSelected]);



  const policeSelectedStyle = {
    fillColor: '#475569',
    weight: 3,
    opacity: 1,
    color: '#1e293b',
    fillOpacity: 0.1,
    dashArray: '5, 5',
    interactive: false
  };

  const localBodyV2Style = {
    fillColor: '#6366f1',
    weight: 3,
    opacity: 1,
    color: '#4f46e5',
    fillOpacity: 0.1,
    interactive: false
  };

  const constituencySelectedStyle = {
    fillColor: '#6366f1',
    weight: 3,
    opacity: 1,
    color: '#4338ca',
    fillOpacity: 0.1,
    interactive: false
  };

  const boltIcon = L.divIcon({
    html: `
      <div class="pulse-tneb"></div>
      <div style="background: #f59e0b; width: 36px; height: 36px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.3); position: relative; z-index: 1;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
      </div>`,
    className: 'custom-bolt-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });

  const policeDotIcon = L.divIcon({
    html: `<div style="background: #334155; width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"></div>`,
    className: 'police-dot-icon',
    iconSize: [12, 12],
    iconAnchor: [6, 6]
  });

  const getPostOfficeIcon = (type: string, delivery: string) => {
    let color = '#10b981'; // Green for Delivery
    if (type === 'HO') color = '#3b82f6'; // Blue for Head Office
    else if (delivery?.toLowerCase().includes('non')) color = '#f59e0b'; // Yellow for Non Delivery

    return L.divIcon({
      html: `
        <div class="pulse-postal" style="background: ${color}"></div>
        <div style="background: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); position: relative; z-index: 1;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
        </div>`,
      className: 'custom-div-icon',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  };

  const healthPriorityIcon = useMemo(() => (type: string) => {
    const tierConfig: Record<string, { color: string; size: number; weight: number; showIcon: boolean }> = {
      'MCH': { color: '#9d174d', size: 40, weight: 4, showIcon: true },   // Medical College
      'DH':  { color: '#be123c', size: 36, weight: 3.5, showIcon: true }, // District Hospital
      'SDH': { color: '#e11d48', size: 30, weight: 3, showIcon: true },   // Sub-District Hospital
      'CHC': { color: '#f43f5e', size: 26, weight: 2.5, showIcon: true }, // Community Health Centre
      'PHC': { color: '#be123c', size: 22, weight: 2.5, showIcon: true }, // Primary Health Centre
      'HSC': { color: '#e11d48', size: 18, weight: 2, showIcon: false }   // Health Sub Centre
    };

    const { color, size, weight, showIcon } = tierConfig[type] || { color: '#f43f5e', size: 22, weight: 2.5, showIcon: true };
    const touchSize = Math.max(34, size + 8);

    return L.divIcon({
      html: `
        <div style="width: ${touchSize}px; height: ${touchSize}px; display: flex; align-items: center; justify-content: center; background: transparent; position: relative;">
          <div style="background: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: ${weight}px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.35); position: relative; z-index: 1;">
            ${showIcon && size >= 20 ? `
              <svg width="${size - 10}" height="${size - 10}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
            ` : ''}
          </div>
        </div>`,
      className: 'custom-health-icon',
      iconSize: [touchSize, touchSize],
      iconAnchor: [touchSize / 2, touchSize / 2]
    });
  }, []);

  const selectedHealthIcon = useMemo(() => L.divIcon({
    html: `
      <div class="pulse-health"></div>
      <div style="background: #9d174d; width: 38px; height: 38px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px rgba(0,0,0,0.45); position: relative; z-index: 10;">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      </div>`,
    className: 'selected-health-icon',
    iconSize: [38, 38],
    iconAnchor: [19, 19]
  }), []);

  const veterinaryIcon = useMemo(() => (type: string) => {
    const tierConfig: Record<string, { color: string; size: number; weight: number }> = {
      'POLY_CLINIC': { color: '#7c3aed', size: 22, weight: 2 },
      'CLINIC_CENTER': { color: '#db2777', size: 20, weight: 2 },
      'HOSPITAL': { color: '#ef4444', size: 18, weight: 1.5 },
      'DISPENSARY': { color: '#3b82f6', size: 16, weight: 1.5 },
      'SUB_CENTER': { color: '#10b981', size: 14, weight: 1 }
    };
    
    const { color, size, weight } = tierConfig[type] || { color: '#64748b', size: 14, weight: 1 };
    
    return L.divIcon({
      html: `<div style="background: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: ${weight}px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.25);"></div>`,
      className: 'custom-vet-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  }, []);

  const selectedVeterinaryIcon = useMemo(() => (type: string) => {
    const tierColors: Record<string, string> = {
      'POLY_CLINIC': '#7c3aed',
      'CLINIC_CENTER': '#db2777',
      'HOSPITAL': '#ef4444',
      'DISPENSARY': '#3b82f6',
      'SUB_CENTER': '#10b981'
    };
    const color = tierColors[type] || '#64748b';
    
    return L.divIcon({
      html: `
        <div class="pulse-postal" style="background: ${color};"></div>
        <div style="background: ${color}; width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.4); position: relative; z-index: 10;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 20a2 2 0 0 1-2-2v-1c0-1.1.9-2 2-2h2a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-2z"/><path d="M6.3 13.1a2 2 0 0 1-2.8 0 2 2 0 0 1 0-2.8l1.4-1.4a2 2 0 0 1 2.8 0 2 2 0 0 1 0 2.8z"/><path d="M17.7 13.1a2 2 0 0 0 2.8 0 2 2 0 0 0 0-2.8l-1.4-1.4a2 2 0 0 0-2.8 0 2 2 0 0 0 0 2.8z"/><path d="M9 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/><path d="M15 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
          </svg>
        </div>`,
      className: 'selected-vet-icon',
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });
  }, []);

  const selectedRegistrationIcon = useMemo(() => L.divIcon({
    html: `
      <div class="pulse-registration"></div>
      <div style="background: #4f46e5; width: 36px; height: 36px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.4); position: relative; z-index: 10;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 7h-3a2 2 0 0 1-2-2V2"></path>
          <path d="M9 18H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10l4 4v3.5"></path>
          <path d="M16 22h5v-5"></path>
          <path d="m11 22 10-10-3-3-10 10v3h3Z"></path>
        </svg>
      </div>`,
    className: 'selected-registration-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  }), []);

  const memoizedHealthMarkers = useMemo(() => {
    const data = healthScope === 'STATE' ? healthPriorityData : healthDistrictData;
    if (!data) return null;

    return data.features
      .filter((f: HealthFacility) => {
        if (!selectedHealthFacility) return true;
        const isSameNin = f.properties.nin_number && f.properties.nin_number === selectedHealthFacility.properties.nin_number;
        const isSameId = f.id && f.id === selectedHealthFacility.id;
        return !(isSameNin || isSameId);
      })
      .map((f: HealthFacility, i: number) => {
        const [lng, lat] = f.geometry.coordinates;

      return (
        <Marker
          key={`health-marker-${f.properties.nin_number || f.id || i}`}
          position={[lat, lng]}
          icon={healthPriorityIcon(f.properties.facility_t)}
          keyboard={true}
          alt={f.properties.facility_n}
          title={f.properties.facility_n}
          eventHandlers={{
            click: (e) => {
              stopLeafletClickPropagation(e);
              setSelectedHealthFacility(f);
              const dist = (f.properties.district || f.properties.district_n)?.toString() || activeDistrict;
              resolveHealthFacility((f.id || f.properties.ogc_fid || 0) as string | number, f.properties.nin_number as string | number | undefined, dist);
            }
          }}
        >
          <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false}>
            <div style={{ padding: '4px 8px' }}>
              <div style={{ fontWeight: 'bold' }}>{f.properties.facility_n}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{t(f.properties.facility_t as TranslationKey)}</div>
            </div>
          </Tooltip>
        </Marker>
      );
    });
  }, [healthScope, healthPriorityData, healthDistrictData, activeDistrict, healthPriorityIcon, resolveHealthFacility, loadHealthDistrict, selectedHealthFacility, t]);



  const memoizedPoliceMarkers = useMemo(() => {
    if (!policeStationsData) return null;
    return policeStationsData.features.map((f: GisFeature<Point, PoliceStationProperties>, i: number) => {
      const [lng, lat] = f.geometry.coordinates as [number, number];

      return (
        <Marker
          key={`police-marker-${f.properties.ps_code || f.id || i}`}
          position={[lat, lng]}
          icon={policeDotIcon}
          keyboard={true}
          alt={f.properties.ps_name || t('POLICE')}
          title={f.properties.ps_name || t('POLICE')}
          eventHandlers={{
            click: (e) => {
              stopLeafletClickPropagation(e);
              resolveLocation(lat, lng, 'POLICE', false, undefined, f.properties.ps_code);
            }
          }}
        >
          <Tooltip direction="top" offset={[0, -5]} opacity={1}>
            <div style={{ padding: '5px' }}>
              <strong>{f.properties.ps_name || t('POLICE')}</strong>
              <br />
              <small>{t('CLICK_TO_VIEW_JURISDICTION')}</small>
            </div>
          </Tooltip>
        </Marker>
      );
    });
  }, [policeStationsData, policeDotIcon, resolveLocation, t]);

  const memoizedVeterinaryMarkers = useMemo(() => {
    if (!veterinaryData) return null;
    return veterinaryData.features
      .filter((f: VeterinaryFacility) => {
        const isActiveType = veterinaryFilters.includes(f.properties.type);
        if (!isActiveType) return false;
        
        if (!selectedVeterinaryFacility) return true;
        const isSameName = f.properties.village_na === selectedVeterinaryFacility.properties.village_na;
        const isSameTaluk = f.properties.taluk_name === selectedVeterinaryFacility.properties.taluk_name;
        return !(isSameName && isSameTaluk);
      })
      .map((f: VeterinaryFacility, i: number) => {
        const [lng, lat] = f.geometry.coordinates;

        return (
          <Marker
            key={`vet-marker-${f.properties.village_na}-${f.properties.taluk_name}-${i}`}
            position={[lat, lng]}
            icon={veterinaryIcon(f.properties.type)}
            keyboard={true}
            alt={f.properties.village_na}
            title={f.properties.village_na}
            eventHandlers={{
              click: (e) => {
                stopLeafletClickPropagation(e);
                const store = useMapStore.getState();
                store.setNoDataFound(false);
                store.setSearchResult(null);
                setSelectedVeterinaryFacility(f);
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -5]} opacity={1}>
              <div style={{ padding: '4px 8px' }}>
                <div style={{ fontWeight: 'bold' }}>{f.properties.village_na}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>{f.properties.taluk_name} {f.properties.type?.replace('_', ' ')}</div>
              </div>
            </Tooltip>
          </Marker>
        );
      });
  }, [veterinaryData, veterinaryFilters, veterinaryIcon, setSelectedVeterinaryFacility, selectedVeterinaryFacility]);

  const selectedTempleIcon = useMemo(() => {
    if (!selectedTemple) return undefined;
    const t = selectedTemple as EnrichedTemple;

    // 1. Color (Age)
    let color = '#86198f'; // Purple for newer/unknown
    const cen = t.c?.hist?.en?.cen;
    if (cen) {
      const c = String(cen).trim().toLowerCase();
      const isPre10th = /before|(\b(1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)\b)/i.test(c) && 
                        !/(\b(11th|12th|13th|14th|15th|16th|17th|18th|19th|20th|21st)\b)/i.test(c);
      const isMedieval = /(\b(11th|12th|13th|14th|15th)\b)/i.test(c);
      
      if (isPre10th) {
        color = '#c2410c'; // Terracotta/Rust
      } else if (isMedieval) {
        color = '#78350f'; // Dark Ochre/Brown
      }
    }

    // 2. Border (Heritage)
    const isHeritage = t.c?.hist?.en?.her === 'Yes' || t.c?.hist?.en?.her === 'Y';
    const borderStyle = isHeritage 
      ? 'border: 2.5px solid #eab308; box-shadow: 0 0 12px #eab308, 0 4px 12px rgba(0,0,0,0.4);' 
      : 'border: 2.5px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';

    return L.divIcon({
      html: `
        <div class="pulse-temple"></div>
        <div style="width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; background: transparent; position: relative;">
          <div style="background: ${color}; width: 38px; height: 38px; border-radius: 50%; ${borderStyle} display: flex; align-items: center; justify-content: center; position: relative; z-index: 10;">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="22" x2="21" y2="22"></line>
            <line x1="6" y1="18" x2="6" y2="11"></line>
            <line x1="10" y1="18" x2="10" y2="11"></line>
            <line x1="14" y1="18" x2="14" y2="11"></line>
            <line x1="18" y1="18" x2="18" y2="11"></line>
            <polygon points="12 2 20 7 4 7"></polygon>
          </svg>
          </div>
        </div>`,
      className: 'selected-temple-icon',
      iconSize: [46, 46],
      iconAnchor: [23, 23]
    });
  }, [selectedTemple]);

  const guidanceOverlayData = useMemo(() => {
    if (!showDeepLinkHint || !searchResult || !searchResult.geometry) return null;

    const geometry = searchResult.geometry as GisPolygon | GisMultiPolygon;
    // Massive rectangle covering any visible part of the map
    const worldOuter: L.LatLngExpression[] = [
      [-90, -200], [-90, 200], [90, 200], [90, -200], [-90, -200]
    ];

    const rings: L.LatLngExpression[][] = [worldOuter];

    try {
      if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring: [number, number][]) => {
          // Reverse order: Leaflet needs [lat, lng]
          rings.push(ring.map(p => [p[1], p[0]] as L.LatLngTuple));
        });
      } else if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((poly: [number, number][][]) => {
          poly.forEach((ring: [number, number][]) => {
            rings.push(ring.map(p => [p[1], p[0]] as L.LatLngTuple));
          });
        });
      }

      const centroid = L.geoJSON(searchResult).getBounds().getCenter();

      return { rings, centroid };
    } catch (e) {
      console.warn('Failed to build inverse mask', e);
      return null;
    }
  }, [showDeepLinkHint, searchResult]);

  const guidanceHandIcon = useMemo(() => L.divIcon({
    html: `
      <div style="position: relative; width: 64px; height: 64px; display: flex; align-items: center; justify-content: center; pointer-events: none;">
        <div class="guidance-ripple"></div>
        <div class="guidance-hand-icon" style="background: #2563eb; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 6px 16px rgba(0,0,0,0.25);">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
             <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
             <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
             <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
             <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
          </svg>
        </div>
      </div>
    `,
    className: 'custom-guidance-hand',
    iconSize: [64, 64],
    iconAnchor: [32, 32]
  }), []);

  const tnBounds: L.LatLngBoundsLiteral = [
    [6.0, 74.0], // Southwest - Expanded for breathing room
    [15.5, 83.0] // Northeast - Expanded for breathing room
  ];

  const searchProps = searchResult?.properties as (GisProperties & { main_text?: string; name?: string }) | undefined;

  return (
    <MapContainer
      center={[11.1271, 78.6569]}
      zoom={7}
      minZoom={6}
      maxBounds={tnBounds}
      maxBoundsViscosity={0.6}
      scrollWheelZoom={true}
      zoomControl={false}
      preferCanvas={true}
      style={{ width: '100%', height: '100%' }}
    >
      <TileLayer
        key={`tile-layer-${theme}`}
        attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
        url={getCartoTileUrl(theme === 'dark' ? 'dark_all' : 'light_all')}
      />

      {/* Background Boundaries based on Active Layer */}
      {(activeLayer === 'PINCODE' || activeLayer === 'TEMPLES' || (ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL')) && districtsData && (
        <GeoJSON
          key={`districts-${theme}-${isAreaSelected}`}
          data={districtsData}
          style={districtStyle}
          interactive={false}
        />
      )}

      {/* User Location Marker */}
      <UserLocationMarker />

      {(activeLayer === 'PDS' || activeLayer === 'TNEB' || activeLayer === 'POLICE' || activeLayer === 'HEALTH' || activeLayer === 'LOCAL_BODIES_V2' || activeLayer === 'REGISTRATION' || activeLayer === 'TEMPLES' || (ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL')) && stateBoundaryData && (
        <GeoJSON
          key={`state-${theme}-${isAreaSelected}`}
          data={stateBoundaryData}
          style={{ ...districtStyle, weight: 2, fillOpacity: 0.02 }}
          interactive={false}
        />
      )}



      {activeLayer === 'CONSTITUENCY' && (constituencyType === 'AC' ? acData : pcData) && (
        <GeoJSON
          key={`constituencies-${constituencyType}-${theme}-${isAreaSelected}-${Object.keys(electionResults || {}).length}`}
          data={constituencyType === 'AC' ? acData! : pcData!}
          style={getConstituencyStyle}
          interactive={false}
        />
      )}

      {searchResult && activeLayer !== 'FUEL' && !jurisdictionGeometry && !(ENABLE_LAND_PARCEL && selectedLandParcel) && searchResult.geometry && (searchResult.geometry.type === 'Polygon' || searchResult.geometry.type === 'MultiPolygon') && (
        <GeoJSON
          key={`search-result-${searchResult.properties.PIN_CODE || searchResult.properties.pincode || searchResult.properties.office_name || searchResult.properties.NAME || searchResult.properties.ps_code}-${searchResult.geometry.type}`}
          data={searchResult}
          style={activeLayer === 'CONSTITUENCY' ? constituencySelectedStyle : activeLayer === 'POLICE' ? policeSelectedStyle : pincodeStyle}
          interactive={false}
        />
      )}

      {activeLayer === 'PINCODE' && selectedPostalOffices && selectedPostalOffices
        .filter(po => !po.isOutlier)
        .map((po, i) => (
          <Marker
            key={`po-${po.pincode}-${po.officename}-${i}`}
            position={[parseFloat(po.latitude as string), parseFloat(po.longitude as string)]}
            icon={getPostOfficeIcon(po.officetype, po.delivery)}
            keyboard={true}
            alt={po.officename}
            title={po.officename}
            eventHandlers={{
              click: () => {
                setSelectedPostalOffice(po);
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <div style={{ padding: '4px 8px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{po.officename}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{t(getOfficeTypeLabelKey(po.officetype) as TranslationKey)}</div>
              </div>
            </Tooltip>
          </Marker>
        ))}

      {activeLayer === 'TNEB' && jurisdictionGeometry && (
        <>
          <GeoJSON
            key={`tneb-${jurisdictionGeometry.type}-${JSON.stringify((jurisdictionGeometry.coordinates as unknown[])[0])}`}
            data={jurisdictionGeometry}
            style={jurisdictionStyle}
            interactive={false}
          />
          <Marker
            position={
              jurisdictionDetails?.office_location
                ? [jurisdictionDetails.office_location[1], jurisdictionDetails.office_location[0]]
                : L.geoJSON(jurisdictionGeometry).getBounds().getCenter()
            }
            icon={boltIcon}
            keyboard={true}
            alt={t('SECTION_OFFICE')}
            title={t('SECTION_OFFICE')}
          >
            <Tooltip direction="top" offset={[0, -20]} opacity={1}>
              <div style={{ padding: '2px 5px', fontWeight: 'bold' }}>
                {t('SECTION_OFFICE')}
              </div>
            </Tooltip>
          </Marker>
        </>
      )}

      {activeLayer === 'REGISTRATION' && jurisdictionGeometry && (
        <>
          <GeoJSON
            key={`registration-${JSON.stringify((jurisdictionGeometry.coordinates as unknown[])[0])}`}
            data={jurisdictionGeometry}
            style={registrationStyle}
            interactive={false}
          />
          {selectedRegistrationOffice && (
            <Marker
              position={
                selectedRegistrationOffice.geometry.type === 'Point'
                  ? [(selectedRegistrationOffice.geometry.coordinates as [number, number])[1], (selectedRegistrationOffice.geometry.coordinates as [number, number])[0]]
                  : L.geoJSON(jurisdictionGeometry).getBounds().getCenter()
              }
              icon={selectedRegistrationIcon}
              keyboard={true}
              alt={selectedRegistrationOffice.properties.office_nam || selectedRegistrationOffice.properties.sro_name}
              title={selectedRegistrationOffice.properties.office_nam || selectedRegistrationOffice.properties.sro_name}
            >
              <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                <div style={{ padding: '2px 5px', fontWeight: 'bold' }}>
                  {selectedRegistrationOffice.properties.office_nam || selectedRegistrationOffice.properties.sro_name}
                </div>
              </Tooltip>
            </Marker>
          )}
        </>
      )}
      
      {ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && selectedLandParcel && selectedLandParcel.geometry && (
        <GeoJSON
          key={`land-parcel-${JSON.stringify(selectedLandParcel.geometry.coordinates)}`}
          data={selectedLandParcel.geometry}
          style={{
            color: '#d97706',
            weight: 3,
            fillColor: '#fbbf24',
            fillOpacity: 0.3,
            dashArray: '5, 5'
          }}
          interactive={false}
        />
      )}

      {activeLayer === 'POLICE' && jurisdictionGeometry && policeResolution?.isBoundaryValid && (
        <GeoJSON
          key={`police-jurisdiction-${policeResolution.boundary.properties.police_s_1}`}
          data={{
            type: 'Feature',
            geometry: jurisdictionGeometry,
            properties: {}
          } as GisFeature}
          style={{
            color: '#334155',
            weight: 3,
            opacity: 0.8,
            fillColor: '#334155',
            fillOpacity: 0.15,
            dashArray: '5, 5'
          }}
        />
      )}



      {activeLayer === 'POLICE' && policeStationsData && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
        >
          {memoizedPoliceMarkers}
        </MarkerClusterGroup>
      )}

      {activeLayer === 'POLICE' && selectedPoliceStation && (
        <Marker
          position={[
            (selectedPoliceStation.geometry.coordinates as [number, number])[1],
            (selectedPoliceStation.geometry.coordinates as [number, number])[0]
          ]}
          icon={L.divIcon({
            html: `
              <div class="pulse-police"></div>
              <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: #334155; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.4); position: relative; z-index: 10;">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>`,
            className: 'selected-police-icon',
            iconSize: [36, 36],
            iconAnchor: [18, 18]
          })}
          keyboard={false}
          alt={selectedPoliceStation.properties.ps_name}
        />
      )}

      {activeLayer === 'LOCAL_BODIES_V2' && selectedLocalBodyV2 && selectedLocalBodyV2.geometry && (
        <>
          <GeoJSON
            key={`v2-boundary-${selectedLocalBodyV2.properties.id}`}
            data={selectedLocalBodyV2}
            style={localBodyV2Style}
            interactive={false}
          />
          {/* Render specific ward boundary inside the ULB container if resolved */}
          {selectedLocalBodyV2.properties.ward_geometry && (
            <GeoJSON
              key={`v2-ward-boundary-${selectedLocalBodyV2.properties.id}`}
              data={{
                type: 'Feature',
                geometry: selectedLocalBodyV2.properties.ward_geometry,
                properties: {}
              } as GisFeature}
              style={{
                fillColor: '#4f46e5',
                weight: 2,
                opacity: 1,
                color: '#4338ca',
                fillOpacity: 0.25, // Stronger fill to clearly distinguish the sub-zone
                dashArray: '3, 3',
                interactive: false
              }}
            />
          )}
        </>
      )}

      {activeLayer === 'PDS' && pdsData && pdsData.features.map((f: GisFeature, i: number) => {
        const [lng, lat] = f.geometry.coordinates as [number, number];
        return (
          <CircleMarker
            key={`pds-${activeDistrict}-${i}`}
            center={[lat, lng]}
            radius={8}
            pathOptions={{
              fillColor: '#ef4444',
              fillOpacity: 0.9,
              color: 'white',
              weight: 1.5,
              bubblingMouseEvents: false
            }}
            eventHandlers={{
              click: () => {
                setSelectedPdsShop(f as PdsShop);
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -5]} opacity={1}>
              <div style={{ padding: '5px' }}>
                <strong>{f.properties.name || t('PDS')}</strong>
                <br />
                <small>{f.properties.village || t('STATE')}</small>
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {activeLayer === 'PDS' && selectedPdsShop && (
        <Marker
          position={[
            (selectedPdsShop.geometry.coordinates as [number, number])[1],
            (selectedPdsShop.geometry.coordinates as [number, number])[0]
          ]}
          interactive={false}
          icon={L.divIcon({
            html: `
              <div class="pulse-pds"></div>
              <div style="background: #ef4444; width: 18px; height: 18px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); position: relative; z-index: 1;"></div > `,
            className: 'selected-pds-icon',
            iconSize: [18, 18],
            iconAnchor: [9, 9]
          })}
          keyboard={false}
          alt={selectedPdsShop.properties.name}
        />
      )}

      {activeLayer === 'PINCODE' && selectedPostalOffice && (
        <Marker
          position={[
            parseFloat(selectedPostalOffice.latitude as string),
            parseFloat(selectedPostalOffice.longitude as string)
          ]}
          interactive={false}
          icon={L.divIcon({
            html: `
              <div class="pulse-postal"></div>
              <div style="background: #ef4444; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); position: relative; z-index: 1;"></div>`,
            className: 'selected-postal-icon',
            iconSize: [14, 14],
            iconAnchor: [7, 7]
          })}
          keyboard={false}
          alt={selectedPostalOffice.officename}
        />
      )}

      {activeLayer === 'HEALTH' && (healthScope === 'STATE' ? healthPriorityData : healthDistrictData) && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={40}
          showCoverageOnHover={false}
          iconCreateFunction={(cluster: { getChildCount: () => number }) => {
            return L.divIcon({
              html: `<div style="background: #be123c; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">${cluster.getChildCount()}</div>`,
              className: 'health-cluster-icon',
              iconSize: [30, 30]
            });
          }}
        >
          {memoizedHealthMarkers}
        </MarkerClusterGroup>
      )}

      {activeLayer === 'HEALTH' && selectedHealthFacility && (
        <Marker
          position={[
            selectedHealthFacility.geometry.coordinates[1],
            selectedHealthFacility.geometry.coordinates[0]
          ]}
          icon={selectedHealthIcon}
          zIndexOffset={1000}
          keyboard={false}
          alt={selectedHealthFacility.properties.facility_n || selectedHealthFacility.properties.NAME}
          eventHandlers={{
            click: (e) => {
              stopLeafletClickPropagation(e);
            }
          }}
        >
          <Tooltip direction="top" offset={[0, -14]} opacity={1} permanent>
            <div style={{ padding: '4px 8px' }}>
              <div style={{ fontWeight: 'bold' }}>{selectedHealthFacility.properties.facility_n || selectedHealthFacility.properties.NAME}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{t(selectedHealthFacility.properties.facility_t as TranslationKey)}</div>
            </div>
          </Tooltip>
        </Marker>
      )}

      {activeLayer === 'VETERINARY' && veterinaryData && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          showCoverageOnHover={false}
          iconCreateFunction={(cluster: { getChildCount: () => number }) => {
            return L.divIcon({
              html: `<div style="background: #334155; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">${cluster.getChildCount()}</div>`,
              className: 'vet-cluster-icon',
              iconSize: [30, 30]
            });
          }}
        >
          {memoizedVeterinaryMarkers}
        </MarkerClusterGroup>
      )}

      {activeLayer === 'VETERINARY' && selectedVeterinaryFacility && (
        <Marker
          position={[
            selectedVeterinaryFacility.geometry.coordinates[1],
            selectedVeterinaryFacility.geometry.coordinates[0]
          ]}
          icon={selectedVeterinaryIcon(selectedVeterinaryFacility.properties.type)}
          zIndexOffset={1000}
          keyboard={false}
          alt={selectedVeterinaryFacility.properties.village_na}
        >
          <Tooltip direction="top" offset={[0, -14]} opacity={1} permanent>
            <div style={{ padding: '4px 8px' }}>
              <div style={{ fontWeight: 'bold' }}>{selectedVeterinaryFacility.properties.village_na}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{selectedVeterinaryFacility.properties.type?.replace('_', ' ')}</div>
            </div>
          </Tooltip>
        </Marker>
      )}

      {activeLayer === 'TEMPLES' && selectedTemples && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          showCoverageOnHover={false}
          iconCreateFunction={(cluster: { getChildCount: () => number }) => {
            return L.divIcon({
              html: `<div style="background: #a21caf; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">${cluster.getChildCount()}</div>`,
              className: 'temple-cluster-icon',
              iconSize: [30, 30]
            });
          }}
        >
          <TempleMarkers />
        </MarkerClusterGroup>
      )}

      {activeLayer === 'TEMPLES' && selectedTemple && (
        <Marker
          position={[
            parseFloat(selectedTemple.g.lat.toString()),
            parseFloat(selectedTemple.g.lng.toString())
          ]}
          icon={selectedTempleIcon}
          zIndexOffset={1000}
          interactive={false}
          keyboard={false}
          alt={selectedTemple.nm.en}
        >
          <Tooltip direction="top" offset={[0, -14]} opacity={1} permanent>
            <div style={{ padding: '4px 8px' }}>
              <div style={{ fontWeight: 'bold' }}>{selectedTemple.nm.en}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{selectedTemple.c?.mol?.en || selectedTemple.dist.en}</div>
            </div>
          </Tooltip>
        </Marker>
      )}

      {activeLayer === 'RADIO' && radioData && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={40}
        >
          {radioData.features.map((f, i) => {
            const [lng, lat] = f.geometry.coordinates;
            const isSelected = selectedRadioStation && selectedRadioStation.properties.id === f.properties.id;
            
            return (
              <Marker
                key={`radio-marker-${f.properties.id || i}`}
                position={[lat, lng]}
                icon={L.divIcon({
                  html: `
                    ${isSelected ? '<div class="pulse-search"></div>' : ''}
                    <div style="background: ${isSelected ? '#db2777' : '#ec4899'}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); position: relative; z-index: 10;">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="2"></circle>
                        <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"></path>
                      </svg>
                    </div>`,
                  className: 'custom-radio-icon',
                  iconSize: [24, 24],
                  iconAnchor: [12, 12]
                })}
                keyboard={true}
                alt={f.properties.name}
                title={f.properties.name}
                eventHandlers={{
                  click: (e) => {
                    stopLeafletClickPropagation(e);
                    const store = useMapStore.getState();
                    store.setNoDataFound(false);
                    store.setSearchResult(null);
                    store.setSelectedRadioStation(f as any);
                    store.setSearchQuery(f.properties.name);
                    store.setIsRadioPlaying(true);
                  }
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                  <div style={{ padding: '4px 8px' }}>
                    <div style={{ fontWeight: 'bold' }}>{f.properties.name}</div>
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>{f.properties.city}</div>
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      )}

      {activeLayer === 'FUEL' && (() => {
        let centerCoord: [number, number] | null = null;
        if (searchResult?.geometry) {
          const g = searchResult.geometry;
          if (g.type === 'Point') {
            const coords = (g as Point).coordinates;
            if (Array.isArray(coords) && coords.length >= 2 && Number.isFinite(coords[1]) && Number.isFinite(coords[0])) {
              centerCoord = [coords[1], coords[0]];
            }
          } else if (g.type === 'Polygon') {
            const ring = (g as GisPolygon).coordinates[0];
            if (Array.isArray(ring) && ring.length > 0) {
              let sumLat = 0, sumLng = 0, count = 0;
              for (const pt of ring) {
                if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[1]) && Number.isFinite(pt[0])) {
                  sumLng += pt[0];
                  sumLat += pt[1];
                  count++;
                }
              }
              if (count > 0) centerCoord = [sumLat / count, sumLng / count];
            }
          } else if (g.type === 'MultiPolygon') {
            const polys = (g as GisMultiPolygon).coordinates;
            if (Array.isArray(polys) && polys.length > 0 && polys[0].length > 0) {
              const ring = polys[0][0];
              let sumLat = 0, sumLng = 0, count = 0;
              for (const pt of ring) {
                if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[1]) && Number.isFinite(pt[0])) {
                  sumLng += pt[0];
                  sumLat += pt[1];
                  count++;
                }
              }
              if (count > 0) centerCoord = [sumLat / count, sumLng / count];
            }
          }
        }

        if (!centerCoord && userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng)) {
          centerCoord = [userLocation.lat, userLocation.lng];
        }

        if (!centerCoord && selectedFuelStation?.geometry?.type === 'Point') {
          const coords = (selectedFuelStation.geometry as Point).coordinates;
          if (Array.isArray(coords) && coords.length >= 2 && Number.isFinite(coords[1]) && Number.isFinite(coords[0])) {
            centerCoord = [coords[1], coords[0]];
          }
        }

        if (!centerCoord || !Number.isFinite(centerCoord[0]) || !Number.isFinite(centerCoord[1])) {
          return null;
        }

        return (
          <Circle
            key={`fuel-radius-circle-${centerCoord[0]}-${centerCoord[1]}-${fuelRadiusKm}`}
            center={centerCoord}
            radius={(fuelRadiusKm || 5.0) * 1000}
            pathOptions={{
              fillColor: '#10b981',
              fillOpacity: 0.08,
              color: '#10b981',
              weight: 2,
              dashArray: '6, 6'
            }}
          />
        );
      })()}

      {activeLayer === 'FUEL' && fuelDistrictData && (
        <MarkerClusterGroup chunkedLoading maxClusterRadius={40}>
          {fuelDistrictData.features.map((f, i) => {
            const [lng, lat] = f.geometry.coordinates as [number, number];
            const isSelected = !!selectedFuelStation && (selectedFuelStation.id === f.id || selectedFuelStation.properties.id === f.properties.id);
            const icon = getFuelIcon(f.properties.brand, isSelected);

            return (
              <Marker
                key={`fuel-marker-${f.id || i}`}
                position={[lat, lng]}
                icon={icon}
                title={`${f.properties.name || 'Fuel Station'} (${getBrandLabel(f.properties.brand)})`}
                keyboard={true}
                alt={f.properties.name}
                eventHandlers={{
                  click: (e) => {
                    stopLeafletClickPropagation(e);
                    const store = useMapStore.getState();
                    store.setNoDataFound(false);
                    store.setSelectedFuelStation(f as FuelStation);
                    store.setSearchQuery(f.properties.name || '');
                    store.setSearchResult(f as GisFeature, false, true);
                    resolveFuelStation(
                      (f.properties.id ?? f.id ?? '').toString(),
                      f.properties.district?.toString() || null
                    );
                  }
                }}
              />
            );
          })}
        </MarkerClusterGroup>
      )}

      {searchResult && (searchResult.suggestionType === 'GLOBAL_PLACE' || searchResult.suggestionType === 'COORDINATES') && !selectedFuelStation && !selectedHealthFacility && !selectedPdsShop && !selectedPoliceStation && !selectedVeterinaryFacility && !selectedRegistrationOffice && !selectedTemple && (
        <Marker
          position={[
            (searchResult.geometry as Point).coordinates[1],
            (searchResult.geometry as Point).coordinates[0]
          ]}
          icon={L.divIcon({
            html: `
              <div class="pulse-search"></div>
              <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: #10b981; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.4); position: relative; z-index: 10;">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>`,
            className: 'selected-search-icon',
            iconSize: [36, 36],
            iconAnchor: [18, 18]
          })}
          keyboard={false}
          alt={searchProps?.main_text || searchProps?.name}
        >
          <Tooltip direction="top" offset={[0, -18]} opacity={1}>
            <div style={{ padding: '4px 8px' }}>
              <div style={{ fontWeight: 'bold' }}>{searchProps?.main_text || searchProps?.name}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{t(searchResult.suggestionType === 'COORDINATES' ? 'CAT_COORDINATES' : 'CAT_GLOBAL')}</div>
            </div>
          </Tooltip>
        </Marker>
      )}

      <MapController
        result={searchResult}
        geometry={jurisdictionGeometry}
        policeResolution={policeResolution}
        selectedLocalBodyV2={selectedLocalBodyV2}
        selectedHealthFacility={selectedHealthFacility}
        selectedVeterinaryFacility={selectedVeterinaryFacility}
        selectedRegistrationOffice={selectedRegistrationOffice}
        selectedLandParcel={selectedLandParcel}
        selectedTemple={selectedTemple}
        selectedFuelStation={selectedFuelStation}
        view={view}
      />

      {/* Safety check for district data */}
      {activeLayer === 'HEALTH' && isHealthLoading && (
        <div className="map-loading-overlay">
          {t('LOADING_HEALTH')}
        </div>
      )}

      {/* Guidance Spotlight Mask and Marker */}
      {guidanceOverlayData && (
        <>
          <Polygon
            positions={guidanceOverlayData.rings}
            pathOptions={{
              color: 'transparent',
              fillColor: '#000000',
              fillOpacity: theme === 'dark' ? 0.75 : 0.55,
              className: 'guidance-mask'
            }}
            interactive={false}
          />
          <Marker
            position={guidanceOverlayData.centroid}
            icon={guidanceHandIcon}
            interactive={false}
          >
            <Tooltip
              direction="bottom"
              offset={[0, 25]}
              opacity={1}
              permanent={true}
              className="guidance-tooltip-transparent"
            >
              <div className="guidance-label-container">
                {t('DEEP_LINK_HINT')}
              </div>
            </Tooltip>
          </Marker>
        </>
      )}

      <MapEvents onResolve={handleResolve} activeLayer={activeLayer} onInteract={() => setShowDeepLinkHint(false)} />
      <ZoomControl position="bottomright" />

      {/* Health Layer Legend Overlay */}
      {activeLayer === 'HEALTH' && !isHealthLoading && (
        <div style={{
          position: 'absolute',
          bottom: '100px',
          right: '20px',
          zIndex: 1000,
          padding: '12px 16px',
          background: theme === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <span style={{ fontSize: '9px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '1px', display: 'block' }}>
            {t('MAP_HIERARCHY')}
          </span>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#be123c', border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-primary)', fontWeight: 700 }}>{t('MAJOR_HOSPITALS')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#e11d48', border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-primary)', fontWeight: 700 }}>{t('SECONDARY_CARE')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fb7185', border: '1px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-primary)', fontWeight: 700 }}>{t('LOCAL_CENTRES')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Temple Layer Legend Overlay */}
      {activeLayer === 'TEMPLES' && (
        <TempleLegend theme={theme} language={language} t={t} />
      )}
    </MapContainer>
  );
};

export default GisMap;
